import React, { useMemo } from "react";
import "./ChemIndexDashboardBarChart.scss";
import {
  ResponsiveContainer,
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  CartesianGrid,
  Cell,
} from "recharts";
import { useGetChemProductsMonthlyGrowthQuery } from "../../../app/services/dashboardAPI";

const COLORS = [
  "#1b6cd8", "#3f8ef9", "#4ea5ff", "#84c5f4",
  "#1f9fe7", "#25b2dc", "#0d2340", "#0f3d7a",
  "#5b7290", "#7ea6d8", "#a5c2e8", "#c9dcf2",
];

const formatNumber = (num) => {
  if (num >= 1_00_000 && num < 1_000_000) return (num / 1_00_000).toFixed(1) + "L";
  if (num >= 1_000_000) return (num / 1_000_000).toFixed(1) + "M";
  if (num >= 1_000) return (num / 1_000).toFixed(1) + "K";
  return num.toLocaleString();
};

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="custom-chart-tooltip">
        <p className="tooltip-label">{label}</p>
        <div className="tooltip-items">
          {payload.map((entry, index) => (
            <div key={index} className="tooltip-item">
              <span className="dot" style={{ backgroundColor: entry.color }}></span>
              <span className="name">{entry.name}:</span>
              <span className="value">{entry.value.toLocaleString()}</span>
            </div>
          ))}
        </div>
      </div>
    );
  }
  return null;
};

const ChemIndexDashboardGrowthChart = ({ selectedYear }) => {
  const {
    data: growthDataRaw = [],
    isFetching,
    isError,
  } = useGetChemProductsMonthlyGrowthQuery(selectedYear, {
    refetchOnMountOrArgChange: true,
  });

  const chartData = useMemo(() => {
    if (!growthDataRaw || growthDataRaw.length === 0) return [];
    return growthDataRaw.map((item, idx) => ({
      ...item,
      color: COLORS[idx % COLORS.length],
    }));
  }, [growthDataRaw]);

  if (isFetching) return <div className="chart-loading">Updating insights...</div>;
  if (isError) return <div className="chart-error">Unable to load growth data</div>;
  if (!chartData.length) return <div className="chart-no-data">No growth data available for this period</div>;

  return (
    <div className="growth-chart-container">
      <ResponsiveContainer width="100%" height={400}>
        <ComposedChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
          <XAxis 
            dataKey="monthName" 
            axisLine={false} 
            tickLine={false} 
            tick={{ fill: "#64748b", fontSize: 12, fontWeight: 500 }}
          />
          <YAxis
            yAxisId="left"
            axisLine={false}
            tickLine={false}
            tick={{ fill: "#64748b", fontSize: 12 }}
            tickFormatter={formatNumber}
          />
          <YAxis
            yAxisId="right"
            orientation="right"
            axisLine={false}
            tickLine={false}
            tick={{ fill: "#64748b", fontSize: 12 }}
            tickFormatter={formatNumber}
          />
          <Tooltip content={<CustomTooltip />} cursor={{ fill: "#f1f5f9" }} />
          <Legend 
            verticalAlign="top" 
            align="right" 
            iconType="circle"
            wrapperStyle={{ paddingBottom: "20px", fontSize: "12px", fontWeight: 600 }}
          />
          <Bar
            yAxisId="left"
            dataKey="inserts"
            name="Monthly Inserts"
            radius={[4, 4, 0, 0]}
            barSize={30}
          >
            {chartData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
            ))}
          </Bar>
          <Line
            yAxisId="right"
            type="monotone"
            dataKey="total"
            name="Cumulative Growth"
            stroke="#1b6cd8"
            strokeWidth={4}
            dot={{ r: 4, fill: "#1b6cd8", strokeWidth: 2, stroke: "#fff" }}
            activeDot={{ r: 6, strokeWidth: 0 }}
            animationDuration={2000}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
};

export default ChemIndexDashboardGrowthChart;
